/**
 * kcritGateway — WRAPPER-DISCIPLINE-EXT-1
 *
 * Provider-registration shim for the KCRIT cross-vendor failure-mode
 * review layer (Wave 26 Prelude Seat 2).
 *
 * On Seat 1 ship: the only registered provider is the built-in stub
 * returning {verdict:'pass', stub:true}. Wrappers call review()
 * conditionally on isHighStakes; the gateway abstracts which provider
 * answers. Stub pass-through is logged on the decision doc (stub:true).
 *
 * On Seat 2 ship (KCRIT-0): KCRIT-0 registers itself as the real
 * provider via registerProvider(). No wrapper code changes required.
 *
 * Provider interface:
 *   provider.review(envelope) → Promise<KcritVerdict>
 *
 * @typedef {{ verdict: 'pass'|'flag'|'reject', reason?: string, stub?: boolean, providerVersion: string }} KcritVerdict
 */

const STUB_VERSION = 'stub-1.0';

let _provider = null;

const _stubProvider = {
  async review(_envelope) {
    return { verdict: 'pass', stub: true, providerVersion: STUB_VERSION };
  },
};

/**
 * Register a real KCRIT provider (called by KCRIT-0 on module init).
 *
 * @param {{ review: (envelope: object) => Promise<KcritVerdict> }} provider
 */
export function registerProvider(provider) {
  if (typeof provider?.review !== 'function') {
    throw new Error('kcritGateway.registerProvider: provider must have a review(envelope) function');
  }
  _provider = provider;
}

/**
 * Deregister the provider (primarily for test teardown).
 */
export function clearProvider() {
  _provider = null;
}

/**
 * Submit an envelope for KCRIT review.
 * Returns the stub verdict until KCRIT-0 registers a real provider.
 *
 * @param {object} envelope - EnforcerEnvelope or wrapper result
 * @returns {Promise<KcritVerdict>}
 */
export async function review(envelope) {
  const activeProvider = _provider || _stubProvider;
  return activeProvider.review(envelope);
}

export default { review, registerProvider, clearProvider };
